import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

import API from "@/apiConfig";

// ---------------------------------------------------------------------------
// Global axios request interceptor
// Attaches Authorization: Bearer <token> to every /api/ request automatically.
// This is the single source of truth for auth headers — no component needs to
// manage headers manually (though existing manual headers are harmless).
// ---------------------------------------------------------------------------
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("auth_token");
  if (token && config.url && config.url.includes("/api")) {
    config.headers = config.headers || {};
    // Only set if not already explicitly set by the caller
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log("Attaching Bearer token to", config.url);
    }
  }
  return config;
});

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [hostedCollectives, setHostedCollectives] = useState([]);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      // Primary key: auth_token
      // Legacy migration: wagc_session_token → auth_token, wagc_admin_token (JWT)
      let token = localStorage.getItem("auth_token");
      if (!token) {
        const legacySession = localStorage.getItem("wagc_session_token");
        const legacyJwt = sessionStorage.getItem("wagc_admin_token");
        token = legacySession || legacyJwt || null;
        if (token) {
          // Migrate to canonical key
          localStorage.setItem("auth_token", token);
          console.log("Migrated legacy token to auth_token");
        }
      }

      if (!token) {
        setUser(null);
        setHostedCollectives([]);
        setLoading(false);
        return;
      }

      const cfg = { headers: { Authorization: `Bearer ${token}` } };

      const [meRes, colRes] = await Promise.all([
        axios.get(`${API}/auth/me`, cfg).catch(() => ({ data: null })),
        axios.get(`${API}/collectives`).catch(() => ({ data: [] })),
      ]);

      if (meRes.data) {
        setUser(meRes.data);
        const email = meRes.data.email;
        const userId = meRes.data.user_id;
        const hosted = Array.isArray(colRes.data)
          ? colRes.data.filter((c) =>
              c.host_email === email ||
              c.guide_pair?.leader1_id === userId ||
              c.guide_pair?.leader2_id === userId ||
              (Array.isArray(c.leaders) && c.leaders.some((l) => l.email === email))
            )
          : [];
        setHostedCollectives(hosted);
      } else {
        setUser(null);
        setHostedCollectives([]);
      }
    } catch {
      setUser(null);
      setHostedCollectives([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    // Running checkAuth() here causes a 401 race condition.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    const token = localStorage.getItem("auth_token");
    try {
      await axios.post(
        `${API}/auth/logout`,
        {},
        token ? { headers: { Authorization: `Bearer ${token}` } } : { withCredentials: true }
      );
    } catch {}
    localStorage.removeItem("auth_token");
    localStorage.removeItem("wagc_session_token"); // clean up legacy key
    sessionStorage.removeItem("wagc_admin_token");
    setUser(null);
    setHostedCollectives([]);
  };

  const STAFF_ROLES = ["staff", "bookstore_staff", "community_room_staff", "administrative_staff", "links_staff"];
  const isAdmin = user?.role === "admin" || user?.role === "it_admin" || user?.role === "apostle";
  const isSuperAdmin = user?.role === "it_admin" || user?.role === "apostle";
  const isApostle = user?.role === "apostle";
  const isStaff = STAFF_ROLES.includes(user?.role);
  const isHost = hostedCollectives.length > 0;
  const hasElevatedAccess = isAdmin || isHost;
  // True when user is logged in but hasn't set their display name yet (will show modal)
  const needsDisplayName = user !== null && user.display_name === null;

  return (
    <AuthContext.Provider
      value={{
        user,
        isAdmin,
        isSuperAdmin,
        isApostle,
        isStaff,
        isHost,
        hostedCollectives,
        hasElevatedAccess,
        needsDisplayName,
        loading,
        setUser,
        logout,
        refresh: checkAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
